import { useId, useState } from "react";

import { casePagePreviewUrl, type DocumentOut } from "@/api";

import { Select } from "./fields/Select";
import styles from "./SketchPane.module.css";
import { SketchViewer, sketchPageName, sketchPages } from "./SketchViewer";

/** A drawing a person can look at: a picture or a PDF the page route draws — and not a sheet cut from one. */
export function viewableDrawings(documents: readonly DocumentOut[]): DocumentOut[] {
  return documents
    .filter(
      (document) =>
        document.derivedFrom === null &&
        (document.mimeType === "application/pdf" || document.mimeType.startsWith("image/")),
    )
    .sort((a, b) => a.sortIndex - b.sortIndex);
}

/** Folded per Vorgang, as the person left it — open where nothing is kept. */
const FOLDED = "zu";
function storageKey(caseId: string): string {
  return `k60.skizze.${caseId}`;
}
function readFolded(caseId: string): boolean {
  try {
    return window.localStorage.getItem(storageKey(caseId)) === FOLDED;
  } catch {
    return false;
  }
}
function keepFolded(caseId: string, folded: boolean): void {
  try {
    if (folded) window.localStorage.setItem(storageKey(caseId), FOLDED);
    else window.localStorage.removeItem(storageKey(caseId));
  } catch {
    // A browser that keeps nothing folds it for this visit only.
  }
}

interface SketchPaneProps {
  caseId: string;
  documents: readonly DocumentOut[];
}

/**
 * The customer's drawing beside the plan (Task 18; all three UX reviews: „am
 * Planungsschirm gibt es keine Skizze daneben“). The geometry is the error
 * detector (E-13) — and it detects nothing where the drawing it is checked
 * against is a click and a tab away. So the drawing stands in the column
 * beside the board, small, at the top: the board keeps its width at 1440 px,
 * the shape can be compared at a glance, and a press shows it large
 * (`SketchViewer`) without leaving the app. Open where there is a drawing,
 * foldable, and folded as the person left it for this Vorgang.
 *
 * Only to look at — nothing is read from it here (E-54).
 */
export function SketchPane({ caseId, documents }: SketchPaneProps) {
  const id = useId();
  const drawings = viewableDrawings(documents);
  const [folded, setFolded] = useState(() => readFolded(caseId));
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [large, setLarge] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  if (drawings.length === 0) return null;

  const drawing = drawings.find((each) => each.id === chosenId) ?? drawings[0]!;
  const pages = sketchPages(drawing);
  const shownPage = Math.min(page, pages);
  const name = sketchPageName(shownPage, pages);
  const src = casePagePreviewUrl(caseId, drawing.id, shownPage);
  const bodyId = `${id}-body`;

  function toggle(): void {
    const next = !folded;
    setFolded(next);
    keepFolded(caseId, next);
  }

  return (
    <section className={styles.pane} aria-label="Skizze">
      <div className={styles.head}>
        <p className={`label ${styles.title}`}>Skizze</p>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={!folded}
          aria-controls={bodyId}
          aria-label={folded ? "Skizze zeigen" : "Skizze ausblenden"}
          onClick={toggle}
        >
          {folded ? "Zeigen" : "Ausblenden"}
        </button>
      </div>
      {folded ? null : (
        <div id={bodyId} className={styles.body}>
          {drawings.length > 1 ? (
            <label className={styles.choice}>
              <span className={`label ${styles.choiceLabel}`}>Zeichnung</span>
              <Select
                className={styles.select}
                value={drawing.id}
                onChange={(event) => {
                  setChosenId(event.target.value);
                  setPage(1);
                }}
              >
                {drawings.map((each) => (
                  <option key={each.id} value={each.id}>
                    {each.originalFilename}
                  </option>
                ))}
              </Select>
            </label>
          ) : (
            <p className={styles.file}>{drawing.originalFilename}</p>
          )}
          {/* The sheet itself (design 2026-10-24 §5.3): paper in the dark, on
              the drawings' one paper tone — the page, not a frame round it
              (UX-Gutachten 2, B-10). The button round it is the desk's well,
              in the mode, and its ring is the desk's. */}
          <button type="button" className={styles.sheet} onClick={() => setLarge(true)}>
            {failed === src ? (
              <span className={styles.failed}>Seite nicht zu zeigen</span>
            ) : (
              <img
                className={styles.page}
                src={src}
                alt={name}
                decoding="async"
                data-blatt="zeichnung"
                onError={() => setFailed(src)}
              />
            )}
            <span className="visually-hidden"> — groß zeigen</span>
          </button>
          {pages > 1 ? (
            <div className={styles.pages}>
              <button
                type="button"
                className={styles.step}
                aria-label="Vorige Seite"
                aria-disabled={shownPage <= 1 ? "true" : undefined}
                onClick={() => setPage(Math.max(1, shownPage - 1))}
              >
                ‹
              </button>
              {/* Said, not only shown (review T18-3). */}
              <span role="status">
                Seite <span className="num">{shownPage}</span> von <span className="num">{pages}</span>
              </span>
              <button
                type="button"
                className={styles.step}
                aria-label="Nächste Seite"
                aria-disabled={shownPage >= pages ? "true" : undefined}
                onClick={() => setPage(Math.min(pages, shownPage + 1))}
              >
                ›
              </button>
            </div>
          ) : null}
        </div>
      )}
      {large ? (
        <SketchViewer caseId={caseId} document={drawing} initialPage={shownPage} onClose={() => setLarge(false)} />
      ) : null}
    </section>
  );
}
